import { z } from "zod";

import { REGISTRATION_START_YEAR } from "@/data/medical";
import { isValidPhoneForCountry } from "@/services/location/phone-country";
import type { CountryCode } from "libphonenumber-js";
import type { OnboardingDraft } from "@/types/onboarding";

/** Shared with the rest of the app: same zod-based approach as elsewhere. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_UPLOAD_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

export function validateUpload(file: File): string | null {
  if (!ACCEPTED_UPLOAD_TYPES.includes(file.type)) {
    return "Upload a PDF, JPG, PNG or WEBP file.";
  }
  if (file.size > MAX_UPLOAD_BYTES) return "File must be 5 MB or smaller.";
  return null;
}

const required = (label: string) => z.string().trim().min(1, `${label} is required.`);

export type StepId =
  | "basics"
  | "specialization"
  | "education"
  | "practice"
  | "establishment"
  | "registration"
  | "consultation"
  | "identity-proof"
  | "registration-proof"
  | "establishment-proof"
  | "location"
  | "timings"
  | "consultant"
  | "review";

export type StepErrors = Partial<Record<keyof OnboardingDraft, string>>;

const currentYear = () => new Date().getFullYear();

/** Per-step validation. Returns field-keyed messages the steps render inline. */
export function validateStep(step: StepId, draft: OnboardingDraft): StepErrors {
  const errors: StepErrors = {};
  const fail = (key: keyof OnboardingDraft, result: z.SafeParseReturnType<unknown, unknown>) => {
    if (!result.success) errors[key] = result.error.issues[0]?.message ?? "Invalid value.";
  };

  switch (step) {
    case "basics": {
      fail("fullName", required("Full name").safeParse(draft.fullName));
      fail("gender", required("Gender").safeParse(draft.gender));
      fail("countryIso", required("Country").safeParse(draft.countryIso));
      if (!draft.phone.trim()) {
        errors.phone = "Mobile number is required.";
      } else if (
        !isValidPhoneForCountry(
          draft.phone,
          (draft.countryIso || undefined) as CountryCode | undefined,
        )
      ) {
        errors.phone = "Enter a valid mobile number for the selected country.";
      }
      fail("region", required("Registration location").safeParse(draft.region));
      fail("city", required("City").safeParse(draft.city));
      break;
    }
    case "specialization":
      fail("specialization", required("Specialization").safeParse(draft.specialization));
      break;
    case "education": {
      fail("degree", required("Degree").safeParse(draft.degree));
      fail("institute", required("College / institute").safeParse(draft.institute));
      const year = Number(draft.completionYear);
      if (!draft.completionYear.trim()) errors.completionYear = "Year of completion is required.";
      else if (!Number.isInteger(year) || year < REGISTRATION_START_YEAR || year > currentYear())
        errors.completionYear = `Enter a year between ${REGISTRATION_START_YEAR} and ${currentYear()}.`;
      const experience = Number(draft.experience);
      if (draft.experience.trim() === "") errors.experience = "Years of experience is required.";
      else if (!Number.isFinite(experience) || experience < 0 || experience > 70)
        errors.experience = "Experience must be between 0 and 70 years.";
      break;
    }
    case "practice":
      if (!draft.ownership) errors.ownership = "Select how you practise.";
      break;
    case "establishment":
      fail("establishmentName", required("Establishment name").safeParse(draft.establishmentName));
      fail("establishmentCity", required("City").safeParse(draft.establishmentCity));
      fail("locality", required("Locality").safeParse(draft.locality));
      break;
    case "registration": {
      fail(
        "registrationNumber",
        required("Registration number").safeParse(draft.registrationNumber),
      );
      fail(
        "registrationCouncil",
        required("Registration council").safeParse(draft.registrationCouncil),
      );
      const year = Number(draft.registrationYear);
      if (!draft.registrationYear.trim())
        errors.registrationYear = "Registration year is required.";
      else if (!Number.isInteger(year) || year < REGISTRATION_START_YEAR || year > currentYear())
        errors.registrationYear = `Registration year must be between ${REGISTRATION_START_YEAR} and ${currentYear()}.`;
      break;
    }
    case "consultation": {
      const fees = Number(draft.fees);
      if (draft.fees.trim() === "") errors.fees = "Consultation fee is required.";
      else if (!Number.isFinite(fees) || fees < 0) errors.fees = "Enter a valid consultation fee.";
      break;
    }
    case "identity-proof":
      if (!draft.identityProof) errors.identityProof = "Upload an identity proof to continue.";
      break;
    case "registration-proof":
      if (!draft.registrationProof)
        errors.registrationProof = "Upload your medical registration proof to continue.";
      break;
    case "establishment-proof":
      if (!draft.establishmentRole) errors.establishmentRole = "Select what applies to you.";
      else if (!draft.establishmentProof)
        errors.establishmentProof = "Upload one of the accepted proofs to continue.";
      break;
    case "location":
      fail("mapAddress", required("Establishment address").safeParse(draft.mapAddress));
      break;
    case "timings":
      if (!draft.timings.some((day) => day.enabled))
        errors.timings = "Enable at least one day of the week.";
      break;
    case "consultant": {
      if (draft.consultant) {
        const fees = Number(draft.consultant.fees);
        if (draft.consultant.fees.trim() === "")
          errors.consultant = "Consultation fee is required.";
        else if (!Number.isFinite(fees) || fees < 0)
          errors.consultant = "Enter a valid consultation fee.";
      }
      break;
    }
    case "review":
      break;
  }

  return errors;
}
